import {ReactComponentElement, useMemo} from 'react';

import {IModuleContentfulProps} from '../components/ModuleContentful/IModuleContentful';

type TBodyItem = Record<string, any>;
type TReactElement = ReactComponentElement<any, any>;

interface IEnrichedModuleProps extends IModuleContentfulProps {
	productIds?: string[];
	carousel?: TReactElement;
	media?: TReactElement;
	images?: TBodyItem[];
	scrollingStripeBanner?: TReactElement;
	title?: any;
	description?: any;
}

const findItemByTypenames = (items: TBodyItem[], typenames: string[]): TBodyItem | undefined =>
	items.find((item) => typenames.includes(item?.__typename));

const findReactElement = (
	reactElements: TReactElement[],
	item: TBodyItem | undefined,
): TReactElement | undefined => {
	if (!item?.sys?.id) {
		return undefined;
	}

	return reactElements.find((el) => el?.props?.sys?.id === item.sys.id);
};

const extractProductIds = (items: TBodyItem[]): string[] => {
	const ids: string[] = [];

	items.forEach((bodyItem) => {
		if (bodyItem?.__typename === 'Carousel') {
			bodyItem?.itemsCollection?.items?.forEach((ci: any, index: number) => {
				if (ci?.__typename === 'ProductInclude') {
					ids.push(ci.product?.items?.[0]?.id);
					bodyItem.itemsCollection.items[index] = {...ci, index};
				}
			});
		}
	});

	return ids.filter(Boolean);
};

type TEnricher = (result: IEnrichedModuleProps, items: TBodyItem[], reactElements: TReactElement[]) => void;

const attachProductIds: TEnricher = (result, items) => {
	const productIds = extractProductIds(items);

	if (productIds.length) {
		result.productIds = productIds;
	}
};

const attachCarousel: TEnricher = (result, items, reactElements) => {
	result.carousel = findReactElement(reactElements, findItemByTypenames(items, ['Carousel']));
};

const attachMedia: TEnricher = (result, items, reactElements) => {
	result.media = findReactElement(reactElements, findItemByTypenames(items, ['Image', 'Video']));
};

const attachDiscoverMoreImages: TEnricher = (result, items) => {
	result.images = items.filter((item) => item?.__typename !== 'Carousel');
};

const extractScrollingStripe: TEnricher = (result, items, reactElements) => {
	const stripeItem = findItemByTypenames(items, ['ScrollingStripeBanner']);

	result.scrollingStripeBanner = findReactElement(reactElements, stripeItem);
	result.bodyCollection = {
		...result.bodyCollection,
		items: items.filter((i) => i?.__typename !== 'ScrollingStripeBanner'),
		reactElements: reactElements.filter((el) => el?.props?.__typename !== 'ScrollingStripeBanner'),
	};
};

const extractTextElements: TEnricher = (result, items, reactElements) => {
	for (const bodyItem of items) {
		if (bodyItem?.__typename === 'Text') {
			if (bodyItem.kind === 'ModuleTitle') {
				result.title = findReactElement(reactElements, bodyItem);
			}

			if (bodyItem.kind === 'ModuleDescription') {
				result.description = findReactElement(reactElements, bodyItem);
			}
		}
	}
};

const variantEnrichers: Record<string, TEnricher[]> = {
	CarouselModule: [attachProductIds, attachCarousel],
	ProductCarousel: [attachProductIds],
	ImageCarousel: [attachProductIds],
	DiscoverMore: [attachDiscoverMoreImages, attachCarousel],
	TopBannerV1: [extractScrollingStripe],
	ImageVideoText: [attachMedia, extractTextElements],
};

const enrichModuleData = (props: IModuleContentfulProps): IEnrichedModuleProps => {
	const items = props.bodyCollection?.items || [];
	const reactElements = props.bodyCollection?.reactElements || [];
	const result: IEnrichedModuleProps = {...props};

	const enrichers = variantEnrichers[props.variant] || [];
	enrichers.forEach((enricher) => enricher(result, items, reactElements));

	return result;
};

const useModuleData = (props: IModuleContentfulProps): IEnrichedModuleProps =>
	useMemo(() => enrichModuleData(props), [props]);

export default useModuleData;
